import { getDeviceType } from "@/hooks/useDeviceType";
import type { TAbmConsent } from "@/components/Checkout/utils/pendingAbmConsent";
import { DEFAULT_LANG } from "@/services/i18n/config";
import { withTimeout } from "@/utils/withTimeout";
import * as Sentry from "@sentry/react";
import { User, updateProfile } from "firebase/auth";
import { doc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { useFirebaseApp } from "reactfire";

const USER_DOC_WRITE_TIMEOUT_MS = 5000;

type TSaveNewUserInfo = {
  user: User;
  userId: string;
  email: string;
  name: string;
  profilePicUrl?: string;
  countryIso?: string;
  signupLang?: string;
  signupSource?: string;
};

const useFirebaseHandlers = () => {
  const firebaseApp = useFirebaseApp();
  const db = getFirestore(firebaseApp);
  const deviceType = getDeviceType();

  const saveNewUserInfo = async (options: TSaveNewUserInfo) => {
    const {
      user,
      userId,
      email,
      name,
      profilePicUrl,
      countryIso,
      signupLang,
      signupSource,
    } = options;
    const userRef = doc(db, "users", userId);

    const [docWrite, profileWrite] = await Promise.allSettled([
      withTimeout(
        setDoc(
          userRef,
          {
            email: email,
            name: name,
            profile_pic: profilePicUrl ?? "",
            updates_subscribed: true,
            culling_subscribed: true,
            sign_up_email_sent: true,
            device_type: deviceType,
            country_iso: countryIso ? countryIso : "US",
            signup_lang: signupLang ?? DEFAULT_LANG,
            signup_type: signupSource ?? "aftershoot",
          },
          { merge: true },
        ),
        USER_DOC_WRITE_TIMEOUT_MS,
        "saveNewUserInfo.setDoc",
      ),
      updateProfile(user, { displayName: name }),
    ]);

    if (profileWrite.status === "rejected") {
      Sentry.captureException(profileWrite.reason);
    }

    if (docWrite.status === "rejected") throw docWrite.reason;
  };

  const saveGeoISOCode = (newUserId: string, userGeoIsoCode?: string) => {
    if (!userGeoIsoCode) {
      console.log("No userGeoIsoCode");
      return;
    }

    const userRef = doc(db, "users", newUserId);

    setDoc(
      userRef,
      {
        country_iso: userGeoIsoCode ?? "US",
      },
      { merge: true },
    );
  };

  const updateUserName = (userId: string, newName: string) => {
    const userRef = doc(db, "users", userId);
    setDoc(
      userRef,
      {
        name: newName,
      },
      { merge: true },
    );
  };

  const updateConsent = async (
    userId: string,
    consent: boolean,
    consentSrc: string,
  ) => {
    const userRef = doc(db, "users", userId);
    return setDoc(
      userRef,
      {
        termsAndPrivacyAccepted: consent,
        termsAndPrivacyAcceptedSource: consentSrc,
      },
      { merge: true },
    );
  };

  const updateAbmConsent = async (userId: string, consent: TAbmConsent) => {
    const userRef = doc(db, "users", userId);
    return setDoc(
      userRef,
      {
        abmCancellationFeeConsent: {
          ...consent,
          source: "web",
          recordedAt: serverTimestamp(),
        },
      },
      { merge: true },
    );
  };

  const saveReferrerCode = async (userId: string, referrerCode: string) => {
    const refCodeRef = doc(db, "refcodes", userId);
    return setDoc(refCodeRef, { referrer_code: referrerCode }, { merge: true });
  };

  return {
    saveNewUserInfo,
    saveGeoISOCode,
    updateUserName,
    updateConsent,
    updateAbmConsent,
    saveReferrerCode,
  };
};

export default useFirebaseHandlers;
